import { type ReactNode, useSyncExternalStore } from "react";
import {
  ArrowUpRight01Icon,
  CentralIcon,
} from "~/components/icons/central-icons";
import { Skeleton } from "~/components/ui/skeleton";
import {
  formatLanguageDate,
  formatLanguageTime,
} from "~/lib/language-lens/intl";
import { cn } from "~/lib/utils";
import { m } from "~/lib/i18n";

type MarketNewsItemLinkMode = "row" | "title";
type MarketNewsBadgePlacement = "above-title" | "below-title";
type MarketNewsItemVariant = "plain" | "tile";

const MARKET_NEWS_MINUTE_MS = 60 * 1000;
const MARKET_NEWS_HOUR_MS = 60 * MARKET_NEWS_MINUTE_MS;

const MARKET_NEWS_SHORT_DATE_OPTIONS = {
  day: "numeric",
  month: "short",
} satisfies Intl.DateTimeFormatOptions;

const MARKET_NEWS_TIME_OPTIONS = {
  hour: "numeric",
  minute: "2-digit",
} satisfies Intl.DateTimeFormatOptions;

const MARKET_NEWS_PUBLICATION_OPTIONS = {
  ...MARKET_NEWS_SHORT_DATE_OPTIONS,
  ...MARKET_NEWS_TIME_OPTIONS,
  timeZone: "UTC",
  timeZoneName: "short",
} satisfies Intl.DateTimeFormatOptions;

const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

function isSameLocalDate(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function isYesterdayLocalDate(date: Date, now: Date): boolean {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return isSameLocalDate(date, yesterday);
}

export function formatMarketNewsTimestamp(
  value: Date | number | string | null | undefined,
  options?: { now?: Date | number },
): string {
  if (value === null || value === undefined) {
    return "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? value : "";
  }

  const now =
    options?.now instanceof Date
      ? options.now
      : new Date(options?.now ?? Date.now());
  if (Number.isNaN(now.getTime())) {
    return formatLanguageDate(date, MARKET_NEWS_SHORT_DATE_OPTIONS);
  }

  const elapsedMs = now.getTime() - date.getTime();
  if (elapsedMs >= 0 && elapsedMs < MARKET_NEWS_HOUR_MS) {
    const elapsedMinutes = Math.floor(elapsedMs / MARKET_NEWS_MINUTE_MS);
    if (elapsedMinutes < 1) {
      return "Just now";
    }

    return `${elapsedMinutes} minute${elapsedMinutes === 1 ? "" : "s"} ago`;
  }

  if (isSameLocalDate(date, now)) {
    return `Today ${formatLanguageTime(date, MARKET_NEWS_TIME_OPTIONS)}`;
  }

  if (isYesterdayLocalDate(date, now)) {
    return `Yesterday ${formatLanguageTime(date, MARKET_NEWS_TIME_OPTIONS)}`;
  }

  return formatLanguageDate(date, MARKET_NEWS_SHORT_DATE_OPTIONS);
}

export function MarketNewsTimestamp(props: {
  value: Date | number | string | null | undefined;
}) {
  // Cached HTML and the first hydration render share a clock-independent UTC
  // label. React then switches to the browser's existing relative/local wording,
  // including when a Suspense boundary hydrates after the rest of the page.
  const hasHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedSnapshot,
    getServerHydrationSnapshot,
  );
  const date =
    props.value === null || props.value === undefined
      ? null
      : props.value instanceof Date
        ? props.value
        : new Date(props.value);
  const validDate = date && !Number.isNaN(date.getTime()) ? date : null;

  return (
    <time
      className="inline-block min-w-36 shrink-0 whitespace-nowrap text-right tabular-nums"
      dateTime={validDate?.toISOString()}
    >
      {hasHydrated || !validDate
        ? formatMarketNewsTimestamp(props.value)
        : formatLanguageDate(validDate, MARKET_NEWS_PUBLICATION_OPTIONS)}
    </time>
  );
}

export function MarketNewsLoadingList(props: {
  className?: string;
  count?: number;
  label?: string;
  rowClassName?: string;
  skeletonClassName?: string;
  trimEdgePadding?: boolean;
}) {
  const count = props.count ?? 3;
  const shouldTrimEdgePadding =
    props.trimEdgePadding ?? props.rowClassName === undefined;

  return (
    <div
      aria-label={props.label ?? m.components_market_news_loading_list_label()}
      className={cn("divide-y divide-border/55", props.className)}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          className={cn(
            "py-3",
            shouldTrimEdgePadding && "first:pt-0 last:pb-0",
            props.rowClassName,
          )}
          key={index}
        >
          <div className="min-w-0">
            <Skeleton
              className={cn("h-4 w-11/12 rounded-md", props.skeletonClassName)}
            />
            <Skeleton
              className={cn(
                "mt-2 h-4 w-7/12 rounded-md",
                props.skeletonClassName,
              )}
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <Skeleton
                className={cn("h-3 w-20 rounded-md", props.skeletonClassName)}
              />
              <Skeleton
                className={cn("h-3 w-16 rounded-md", props.skeletonClassName)}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function MarketNewsEmpty(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={cn("py-3 text-meta text-muted-foreground", props.className)}>
      {props.children}
    </p>
  );
}

export function MarketNewsList(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("divide-y divide-border/55", props.className)}>
      {props.children}
    </div>
  );
}

export function MarketNewsItem(props: {
  backdropImageUrl?: string | null;
  badges?: ReactNode;
  badgesPlacement?: MarketNewsBadgePlacement;
  className?: string;
  contentClassName?: string;
  description?: ReactNode;
  externalIconClassName?: string;
  footer?: ReactNode;
  href: string;
  linkMode?: MarketNewsItemLinkMode;
  meta?: ReactNode;
  rel?: string;
  showExternalIcon?: boolean;
  target?: string;
  title: ReactNode;
  titleClassName?: string;
  variant?: MarketNewsItemVariant;
}) {
  const linkMode = props.linkMode ?? "title";
  const badgesPlacement = props.badgesPlacement ?? "above-title";
  const linkRel = props.rel ?? "noreferrer";
  const linkTarget = props.target ?? "_blank";
  const variant = props.variant ?? "plain";
  const badges = props.badges ? (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        linkMode === "row" && "relative z-10 w-fit",
        badgesPlacement === "above-title" ? "mb-2" : "mt-3",
      )}
    >
      {props.badges}
    </div>
  ) : null;
  const titleClassName = cn(
    "text-body font-normal text-foreground",
    linkMode === "row" ? "group-hover:underline" : "hover:underline",
    variant !== "plain" && "font-medium",
    props.titleClassName,
  );
  const title = (
    <a
      className={cn(
        titleClassName,
        linkMode === "row" &&
          "after:absolute after:inset-0 after:content-[''] focus-visible:outline-none glider-focus-ring-stretched",
      )}
      href={props.href}
      rel={linkRel}
      target={linkTarget}
    >
      {props.title}
    </a>
  );
  const body = (
    <div className={cn("min-w-0", props.contentClassName)}>
      {badgesPlacement === "above-title" ? badges : null}
      {title}
      {badgesPlacement === "below-title" ? badges : null}
      {props.description ? (
        <div className="mt-2 text-meta text-muted-foreground">
          {props.description}
        </div>
      ) : null}
      {props.meta ? (
        <div className="mt-3 text-caption text-muted-foreground">
          {props.meta}
        </div>
      ) : null}
      {props.footer ? (
        <div className={cn(linkMode === "row" && "relative z-10")}>
          {props.footer}
        </div>
      ) : null}
    </div>
  );

  /* Backdrop needs the tile's card boundary to clip against; plain rows have none. */
  const backdrop =
    variant === "tile" && props.backdropImageUrl ? (
      <img
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-[72%] object-cover opacity-[0.3] blur-[1px] saturate-[0.8] brightness-[1.05] contrast-[0.95] transition-opacity duration-200 [mask-image:linear-gradient(to_left,rgba(0,0,0,0.95)_22%,transparent_94%)] group-hover:opacity-[0.38] dark:opacity-40 dark:blur-none dark:saturate-[0.7] dark:brightness-100 dark:contrast-[0.92] dark:group-hover:opacity-50"
        loading="lazy"
        referrerPolicy="no-referrer"
        src={props.backdropImageUrl}
      />
    ) : null;

  if (linkMode === "row") {
    return (
      <article
        className={cn(
          "group relative block transition-colors",
          variant === "plain" && "py-3 first:pt-0 last:pb-0",
          variant === "tile" &&
            "page-surface-tile rounded-lg p-3 hover:bg-surface-3",
          backdrop !== null && "isolate overflow-hidden",
          props.className,
        )}
      >
        {backdrop}
        {body}
        {props.showExternalIcon ? (
          <CentralIcon
            className={cn(
              "pointer-events-none absolute size-4 text-muted-foreground transition-colors group-hover:text-foreground",
              variant === "plain" ? "right-0 top-3" : "right-3 top-3",
              props.externalIconClassName,
            )}
            icon={ArrowUpRight01Icon}
          />
        ) : null}
      </article>
    );
  }

  return (
    <article className={cn("py-3 first:pt-0 last:pb-0", props.className)}>
      {body}
    </article>
  );
}
